import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Combobox, Icon, Input } from '@node-space/ui-components'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import {
  AddBeneficiaryFormErrors,
  BaseFormProps,
  PaymentMethod,
  USDBeneficiaryOptions,
  ValidateRoutingNumberBankDetails,
  ValidateRoutingNumberResponse,
} from 'types/beneficiaries'
import { DEFAULT_CBIT_BANK_CODE } from '../schemas/fields/usdSpecificFields'

interface UsdFormFieldsProps extends BaseFormProps {
  abaRoutingNumber: string
  cubixAccountNumberDigits: string
  cbitBankCode: string
  errors: AddBeneficiaryFormErrors
  usdAccountNumber: string
  usdAccountNumberConfirm?: string
  accountType?: string
  beneficiaryPaymentMethod?: PaymentMethod
  addressLine1: string
  addressLine2: string
  city: string
  postCode: string
  region: string
  isValidatingRoutingNumber?: boolean
  usBankDetails?: ValidateRoutingNumberResponse
  onRoutingNumberBlur: () => void
}

export const UsdFormFields = ({
  abaRoutingNumber,
  cubixAccountNumberDigits,
  errors,
  usdAccountNumber,
  usdAccountNumberConfirm,
  accountType,
  beneficiaryPaymentMethod,
  isValidatingRoutingNumber,
  usBankDetails,
  handleChange,
  onRoutingNumberBlur,
  register,
}: UsdFormFieldsProps) => {
  const { accountCapabilities } = useAccountsContext()
  const {
    isCubixBeneficiaryCapabilityEnabled,
    isUsdCRNowBeneficiaryCapabilityEnabled,
    isUsdRtpBeneficiaryCapabilityEnabled,
    isUsdFedNowBeneficiaryCapabilityEnabled,
  } = accountCapabilities ?? {}

  const { t } = useTranslation()

  const accountTypeOptions = [
    {
      leftText: t('Checking'),
      value: 'CHECKING',
    },
    {
      leftText: t('Savings'),
      value: 'SAVINGS',
    },
  ]

  useEffect(() => {
    if (!beneficiaryPaymentMethod) {
      handleChange('beneficiaryPaymentMethod', USDBeneficiaryOptions.ACH)
    }
  }, [beneficiaryPaymentMethod])

  useEffect(() => {
    if (beneficiaryPaymentMethod === USDBeneficiaryOptions.CUBIX) {
      handleChange('cbitBankCode', DEFAULT_CBIT_BANK_CODE)
    } else {
      handleChange('cbitBankCode', '')
    }
  }, [beneficiaryPaymentMethod])

  useEffect(() => {
    if (!!abaRoutingNumber && !!usBankDetails && !usBankDetails?.validationFailed?.length) {
      setUsdBankAddress(usBankDetails?.bankDetails)
    } else {
      !abaRoutingNumber && resetUsdBankAddress()
    }
  }, [usBankDetails, abaRoutingNumber])

  const setUsdBankAddress = (bankDetails: ValidateRoutingNumberBankDetails) => {
    handleChange('bankName', bankDetails?.bankName)
    handleChange('bankAddressLine1', bankDetails?.bankAddress?.addressLine1)
    handleChange('bankAddressLine2', bankDetails?.bankAddress?.addressLine2)
    handleChange('bankCity', bankDetails?.bankAddress?.city)
    handleChange('bankRegion', bankDetails?.bankAddress?.region)
    handleChange('bankPostCode', bankDetails?.bankAddress?.postCode)
    handleChange('bankCountry', bankDetails?.bankAddress?.countryCode)
  }

  const resetUsdBankAddress = () => {
    handleChange('bankName', '')
    handleChange('bankCode', '')
    handleChange('bankAddressLine1', '')
    handleChange('bankAddressLine2', '')
    handleChange('bankCity', '')
    handleChange('bankRegion', '')
    handleChange('bankPostCode', '')
    handleChange('bankCountry', '')
  }

  const onBeneficiaryTypeSelection = (type: PaymentMethod) => {
    handleChange('beneficiaryPaymentMethod', type)

    if (type === USDBeneficiaryOptions.CUBIX) {
      handleChange('usdAccountNumber', '')
      resetUsdBankAddress()
    } else {
      handleChange('cubixAccountNumberDigits', '')
    }
  }

  const onAccountNumberPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event?.preventDefault()
    !usdAccountNumber && handleChange('usdAccountNumber', '')
    return false
  }

  const onAccountNumberConfirmPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event?.preventDefault()
    !usdAccountNumberConfirm && handleChange('usdAccountNumberConfirm', '')
    return false
  }

  const usdPaymentMethods = useMemo(() => {
    return [
      {
        leftText: t('manageBeneficiaries.ach'),
        value: USDBeneficiaryOptions.ACH,
      },
      {
        leftText: t('manageBeneficiaries.fedwire'),
        value: USDBeneficiaryOptions.FEDWIRE,
      },
      ...(isUsdCRNowBeneficiaryCapabilityEnabled
        ? [
            {
              leftText: t('manageBeneficiaries.crNow'),
              value: USDBeneficiaryOptions.CRNOW,
            },
          ]
        : []),
      ...(isCubixBeneficiaryCapabilityEnabled
        ? [
            {
              leftText: t('manageBeneficiaries.cubixPaymenthMethodOption'),
              value: USDBeneficiaryOptions.CUBIX,
            },
          ]
        : []),
      ...(isUsdFedNowBeneficiaryCapabilityEnabled
        ? [
            {
              leftText: t('manageBeneficiaries.fedNow'),
              value: USDBeneficiaryOptions.FEDNOW,
            },
          ]
        : []),
      ...(isUsdRtpBeneficiaryCapabilityEnabled
        ? [
            {
              leftText: t('manageBeneficiaries.rtp'),
              value: USDBeneficiaryOptions.RTP,
            },
          ]
        : []),
    ]
  }, [
    isCubixBeneficiaryCapabilityEnabled,
    isUsdCRNowBeneficiaryCapabilityEnabled,
    isUsdRtpBeneficiaryCapabilityEnabled,
    isUsdFedNowBeneficiaryCapabilityEnabled,
  ])

  const isValidRoutingNumber =
    !!usBankDetails?.bankDetails?.bankAddress &&
    !usBankDetails?.validationFailed.length &&
    !!abaRoutingNumber

  return (
    <>
      <Box paddingB={16}>
        <Combobox
          id="currencyType"
          label={t('manageBeneficiaries.selectPaymentMethod')}
          data-testid="payment-method-combobox"
          defaultValue={beneficiaryPaymentMethod ?? usdPaymentMethods?.[0]?.value}
          onChange={onBeneficiaryTypeSelection}
          items={usdPaymentMethods}
          disabled={usdPaymentMethods?.length === 1}
        />
      </Box>

      <Box flex gapX={12} paddingB={16}>
        {beneficiaryPaymentMethod == USDBeneficiaryOptions.CUBIX ? (
          <Box flexCol width="full" gapY={16}>
            <Input
              {...register('cubixAccountNumberDigits')}
              label={t('manageBeneficiaries.cubixAccountNumberLastFourDigits')}
              placeholder={'****'}
              inputMode="text"
              name="cubixAccountNumberDigits"
              testId="input-cbit-wallet-address"
              errorText={
                errors?.cubixAccountNumberDigits?.message || errors?.accountNumber?.message
              }
              error={!!errors?.cubixAccountNumberDigits || !!errors?.accountNumber}
              value={cubixAccountNumberDigits}
              onChange={event => handleChange('cubixAccountNumberDigits', event?.target?.value)}
            />
          </Box>
        ) : (
          <Box flexCol width="full" gapY={16}>
            <Box flexCol width="full">
              <Input
                {...register('abaRoutingNumber')}
                label={t('abaRoutingNumber')}
                placeholder={t('abaRoutingNumberPlaceholder')}
                inputMode="text"
                name="abaRoutingNumber"
                testId="input-aba-routing-number"
                errorText={errors?.abaRoutingNumber?.message}
                error={!!errors?.abaRoutingNumber}
                value={abaRoutingNumber}
                onChange={event => handleChange('abaRoutingNumber', event?.target?.value)}
                onBlur={onRoutingNumberBlur}
                IconButton={
                  isValidRoutingNumber && !isValidatingRoutingNumber ? (
                    <Button variant="ghost" iconElement={<Icon name="Check" />} />
                  ) : null
                }
                iconPosition="right"
                loading={isValidatingRoutingNumber}
              />
            </Box>
            <Box flexCol width="full">
              <Input
                {...register('usdAccountNumber')}
                label={t('accountNumber')}
                placeholder={t('usdAccountNumberPlaceholder')}
                inputMode="text"
                name="usdAccountNumber"
                testId="input-usd-account-number"
                errorText={errors?.usdAccountNumber?.message || errors?.accountNumber?.message}
                error={!!errors?.usdAccountNumber || !!errors?.accountNumber}
                value={usdAccountNumber}
                onChange={event => handleChange('usdAccountNumber', event?.target?.value)}
                onPaste={event => onAccountNumberPaste(event)}
              />
            </Box>
            <Box flexCol width="full">
              <Input
                {...register('usdAccountNumberConfirm')}
                label={t('confirmAccountNumber')}
                placeholder={t('confirmUsdAccountNumberPlaceholder')}
                inputMode="text"
                name="usdAccountNumberConfirm"
                testId="input-usd-account-number-confirm"
                errorText={errors?.usdAccountNumberConfirm?.message}
                error={!!errors?.usdAccountNumberConfirm}
                value={usdAccountNumberConfirm}
                onChange={event => handleChange('usdAccountNumberConfirm', event?.target?.value)}
                onPaste={event => onAccountNumberConfirmPaste(event)}
              />
            </Box>

            <Box flexSize="fill">
              <Combobox
                {...register('accountType')}
                label={t('accountType')}
                id="accountType"
                items={accountTypeOptions}
                onChange={value => handleChange('accountType', value)}
                errorText={errors?.accountType?.message}
                defaultValue={accountType}
              />
            </Box>
          </Box>
        )}
      </Box>
    </>
  )
}
